import React, { createContext, useContext, useEffect, useState } from 'react';

import { CacheKey, setJSONToLocalStorage, getJSONFromLocalStorage } from 'utils/localStorage';
import {
  getSurf3dBalance,
  getWhirlpoolBalance,
  getBeachBalance,
  getPolywaveBalance,
  getEthWalletBalance,
  getBscWalletBalance,
  getXDaiWalletBalance,
  getMaticWalletBalance,
  mergeDappBalances,
  mergeCurrencyBalances,
  convertCurrencyBalance,
  sumCurrencyBalance,
} from 'utils/price';
import { Currency, CurrencyBalance, DappBalance } from 'utils/types';
import { useChain } from './chain';
import { usePrice } from './price';

type Portfolio = {
  ethWalletBalance: CurrencyBalance;
  xDaiWalletBalance: CurrencyBalance;
  bscWalletBalance: CurrencyBalance;
  maticWalletBalance: CurrencyBalance;
  surf3dBalance: DappBalance;
  whirlpoolBalance: DappBalance;
  beachBalance: DappBalance;
  polywaveBalance: DappBalance;
};

type ContextProps = Portfolio & {
  accounts: string[];
  ethWalletBalanceUsd: CurrencyBalance;
  xDaiWalletBalanceUsd: CurrencyBalance;
  bscWalletBalanceUsd: CurrencyBalance;
  maticWalletBalanceUsd: CurrencyBalance;
  surf3dBalanceCurrency: CurrencyBalance;
  surf3dBalanceTotalUsd: number;
  whirlpoolBalanceCurrency: CurrencyBalance;
  whirlpoolBalanceTotalUsd: number;
  beachBalanceCurrency: CurrencyBalance;
  beachBalanceTotalUsd: number;
  polywaveBalanceCurrency: CurrencyBalance;
  polywaveBalanceTotalUsd: number;
  walletBalance: CurrencyBalance;
  walletBalanceUsd: CurrencyBalance;
  walletBalanceTotalUsd: number;
  lockedBalance: CurrencyBalance;
  lockedBalanceUsd: CurrencyBalance;
  lockedBalanceTotalUsd: number;
  unclaimedBalance: CurrencyBalance;
  unclaimedBalanceUsd: CurrencyBalance;
  unclaimedBalanceTotalUsd: number;
  netWorthBalance: CurrencyBalance;
  netWorthBalanceTotalUsd: number;
};

const PortfolioContext = createContext({} as ContextProps);

export const usePortfolio = () => useContext(PortfolioContext);

export const PortfolioProvider: React.FC = (props) => {
  const [accounts, setAccounts] = useState<string[]>(
    getJSONFromLocalStorage(CacheKey.Accounts) || []
  );
  const [portfolio, setPortfolio] = useState<Portfolio>({
    ethWalletBalance: {},
    xDaiWalletBalance: {},
    bscWalletBalance: {},
    maticWalletBalance: {},
    surf3dBalance: {
      locked: {},
      unclaimed: {},
    },
    whirlpoolBalance: {
      locked: {},
      unclaimed: {},
    },
    beachBalance: {
      locked: {},
      unclaimed: {},
    },
    polywaveBalance: {
      locked: {},
      unclaimed: {},
    },
  });
  const { id: chainId, account } = useChain();
  const { ethTokenPrices, xDaiTokenPrices, bscTokenPrices, maticTokenPrices } = usePrice();

  useEffect(() => {
    if (chainId && account) {
      setAccounts((currentAccounts) => {
        if (currentAccounts.includes(account)) {
          return currentAccounts;
        }

        return [...currentAccounts, account];
      });
    }
  }, [chainId, account]);

  useEffect(() => {
    const fetchBalances = async () => {
      const [
        ethWalletBalances,
        xDaiWalletBalances,
        bscWalletBalances,
        maticWalletBalances,
        surf3dBalances,
        whirlpoolBalances,
        beachBalances,
        polywaveBalances,
      ] = await Promise.all([
        Promise.all(accounts.map(getEthWalletBalance)),
        Promise.all(accounts.map(getXDaiWalletBalance)),
        Promise.all(accounts.map(getBscWalletBalance)),
        Promise.all(accounts.map(getMaticWalletBalance)),
        Promise.all(accounts.map(getSurf3dBalance)),
        Promise.all(accounts.map(getWhirlpoolBalance)),
        Promise.all(accounts.map(getBeachBalance)),
        Promise.all(accounts.map(getPolywaveBalance)),
      ]);
      setPortfolio({
        ethWalletBalance: mergeCurrencyBalances(ethWalletBalances),
        xDaiWalletBalance: mergeCurrencyBalances(xDaiWalletBalances),
        bscWalletBalance: mergeCurrencyBalances(bscWalletBalances),
        maticWalletBalance: mergeCurrencyBalances(maticWalletBalances),
        surf3dBalance: mergeDappBalances(surf3dBalances),
        whirlpoolBalance: mergeDappBalances(whirlpoolBalances),
        beachBalance: mergeDappBalances(beachBalances),
        polywaveBalance: mergeDappBalances(polywaveBalances),
      });
    };

    fetchBalances();
    setJSONToLocalStorage(CacheKey.Accounts, accounts);
  }, [accounts]);

  const {
    ethWalletBalance,
    xDaiWalletBalance,
    bscWalletBalance,
    maticWalletBalance,
    surf3dBalance,
    whirlpoolBalance,
    beachBalance,
    polywaveBalance,
  } = portfolio;

  const walletBalance = mergeCurrencyBalances([
    ethWalletBalance,
    xDaiWalletBalance,
    bscWalletBalance,
    maticWalletBalance,
  ]);

  const ethWalletBalanceUsd = convertCurrencyBalance(
    ethWalletBalance,
    ethTokenPrices,
    Currency.USD
  );
  const xDaiWalletBalanceUsd = convertCurrencyBalance(
    xDaiWalletBalance,
    xDaiTokenPrices,
    Currency.USD
  );
  const bscWalletBalanceUsd = convertCurrencyBalance(
    bscWalletBalance,
    bscTokenPrices,
    Currency.USD
  );
  const maticWalletBalanceUsd = convertCurrencyBalance(
    maticWalletBalance,
    maticTokenPrices,
    Currency.USD
  );
  const walletBalanceUsd = mergeCurrencyBalances([
    ethWalletBalanceUsd,
    xDaiWalletBalanceUsd,
    bscWalletBalanceUsd,
    maticWalletBalanceUsd,
  ]);
  const walletBalanceTotalUsd = sumCurrencyBalance(walletBalanceUsd);

  const surf3dBalanceCurrency = mergeCurrencyBalances([
    surf3dBalance.locked,
    surf3dBalance.unclaimed,
  ]);
  const surf3dBalanceUsd = convertCurrencyBalance(
    surf3dBalanceCurrency,
    ethTokenPrices,
    Currency.USD
  );

  const whirlpoolBalanceCurrency = mergeCurrencyBalances([
    whirlpoolBalance.locked,
    whirlpoolBalance.unclaimed,
  ]);
  const whirlpoolBalanceUsd = convertCurrencyBalance(
    whirlpoolBalanceCurrency,
    ethTokenPrices,
    Currency.USD
  );

  const beachBalanceCurrency = mergeCurrencyBalances([beachBalance.locked, beachBalance.unclaimed]);
  const beachBalanceUsd = convertCurrencyBalance(
    beachBalanceCurrency,
    maticTokenPrices,
    Currency.USD
  );

  const polywaveBalanceCurrency = mergeCurrencyBalances([
    polywaveBalance.locked,
    polywaveBalance.unclaimed,
  ]);
  const polywaveBalanceUsd = convertCurrencyBalance(
    polywaveBalanceCurrency,
    maticTokenPrices,
    Currency.USD
  );

  const lockedBalance = mergeCurrencyBalances([
    surf3dBalance.locked,
    whirlpoolBalance.locked,
    beachBalance.locked,
    polywaveBalance.locked,
  ]);
  const lockedBalanceUsd = mergeCurrencyBalances([
    convertCurrencyBalance(surf3dBalance.locked, ethTokenPrices, Currency.USD),
    convertCurrencyBalance(whirlpoolBalance.locked, ethTokenPrices, Currency.USD),
    convertCurrencyBalance(beachBalance.locked, maticTokenPrices, Currency.USD),
    convertCurrencyBalance(polywaveBalance.locked, maticTokenPrices, Currency.USD),
  ]);
  const lockedBalanceTotalUsd = sumCurrencyBalance(lockedBalanceUsd);

  const unclaimedBalance = mergeCurrencyBalances([
    surf3dBalance.unclaimed,
    whirlpoolBalance.unclaimed,
    beachBalance.unclaimed,
    polywaveBalance.unclaimed,
  ]);
  const unclaimedBalanceUsd = mergeCurrencyBalances([
    convertCurrencyBalance(surf3dBalance.unclaimed, ethTokenPrices, Currency.USD),
    convertCurrencyBalance(whirlpoolBalance.unclaimed, ethTokenPrices, Currency.USD),
    convertCurrencyBalance(beachBalance.unclaimed, maticTokenPrices, Currency.USD),
    convertCurrencyBalance(polywaveBalance.unclaimed, maticTokenPrices, Currency.USD),
  ]);
  const unclaimedBalanceTotalUsd = sumCurrencyBalance(unclaimedBalanceUsd);

  return (
    <PortfolioContext.Provider
      {...props}
      value={{
        ...portfolio,
        accounts,
        ethWalletBalanceUsd,
        xDaiWalletBalanceUsd,
        bscWalletBalanceUsd,
        maticWalletBalanceUsd,
        surf3dBalanceCurrency,
        surf3dBalanceTotalUsd: sumCurrencyBalance(surf3dBalanceUsd),
        whirlpoolBalanceCurrency,
        whirlpoolBalanceTotalUsd: sumCurrencyBalance(whirlpoolBalanceUsd),
        beachBalanceCurrency,
        beachBalanceTotalUsd: sumCurrencyBalance(beachBalanceUsd),
        polywaveBalanceCurrency,
        polywaveBalanceTotalUsd: sumCurrencyBalance(polywaveBalanceUsd),
        walletBalance,
        walletBalanceUsd,
        walletBalanceTotalUsd,
        lockedBalance,
        lockedBalanceUsd,
        lockedBalanceTotalUsd,
        unclaimedBalance,
        unclaimedBalanceUsd,
        unclaimedBalanceTotalUsd,
        netWorthBalance: mergeCurrencyBalances([walletBalance, lockedBalance, unclaimedBalance]),
        netWorthBalanceTotalUsd:
          walletBalanceTotalUsd + lockedBalanceTotalUsd + unclaimedBalanceTotalUsd,
      }}
    />
  );
};
